<script lang="ts">
  import { page } from '$app/stores';
  import Button from '$components/Button.svelte';
  import {
    clearPriceAlert,
    populatePriceAlert
  } from '$components/PriceAlertsModal.svelte';
  import FlucChart from '$components/charts/FlucChart.svelte';
  import BarLoader from '$components/spinners/BarLoader.svelte';
  import type {
    BookmakerMarket,
    BookmakerMarketResponse,
    Fluc,
    MarketType,
    Runner
  } from '$types/races';
  import { isSubscribed } from '$utils/auth';
  import { prefillBookmakers } from '$utils/bookmakers';
  import { fetchBookmakerMarket } from '$utils/markets';
  import { isRaceClosed } from '$utils/race';
  import { formatTime } from '$utils/time';
  import { activeSiteConfig } from '$utils/ui';
  import type { ContentAction } from 'svelte-floating-ui';
  import toast from 'svelte-french-toast';
  import type { Readable } from 'svelte/store';
  import TablerChartLine from '~icons/tabler/chart-line';

  $: ({ session, race, streamed, hasCompanion, user, featureFlags } =
    $page.data);
  $: raceLinks = streamed?.raceData?.data?.race.links;
  $: prefillEnabled = $featureFlags?.includes('bet_prefill');

  export let showDialog: boolean;
  export let runner: Runner;
  export let market: Readable<BookmakerMarket> | undefined;
  export let marketType: MarketType;
  export let floatingContent: ContentAction;
  export let loading: boolean;

  let marketData: BookmakerMarketResponse | null = null;

  $: flucs =
    marketData?.data?.bookmakerMarket?.[marketType]?.flucs?.filter((fluc) => {
      return (fluc?.price ?? 0) > 1.01;
    }) ?? [];
  let change = 0;
  $: {
    const firstNonZeroFluc = flucs?.find((fluc) => fluc.price !== 0)?.price;
    // get percentange chance of opening price
    if (!firstNonZeroFluc) {
      change = 0;
    } else {
      const lastPrice = flucs?.[flucs?.length - 1]?.price ?? 0;
      const openingPriceChance = 1 / firstNonZeroFluc;
      const lastPriceChance = 1 / lastPrice;
      const oddsChanceChange = openingPriceChance - lastPriceChance;
      change = Math.round(oddsChanceChange * 1000) / 10;
    }
  }
  let strokeColour = '';
  $: {
    if (change > 0) strokeColour = '#22C55E';
    else if (change < 0) strokeColour = '#EF4444';
    else strokeColour = '#FCD34D';
  }
  $: pos = () => {
    if (change > 0) return 'top';
    else return 'bottom';
  };

  function flucRowClass(fluc: Fluc) {
    // check if the fluc is higher or lower than the previous fluc
    if (!marketData?.data?.bookmakerMarket?.[marketType].flucs) return '';
    const index =
      marketData?.data?.bookmakerMarket?.[marketType]?.flucs?.indexOf(fluc);

    if (!index && !marketData?.data?.bookmakerMarket?.[marketType].openingPrice)
      'bg-yellow-300';
    if (index === undefined) return 'bg-yellow-300';
    const previousPrice =
      index === 0 &&
      marketData?.data?.bookmakerMarket?.[marketType].openingPrice
        ? marketData?.data?.bookmakerMarket?.[marketType].openingPrice
        : marketData?.data?.bookmakerMarket?.[marketType]?.flucs?.[index - 1]
            ?.price;
    const isHigher =
      (marketData?.data?.bookmakerMarket?.[marketType]?.flucs?.[index]?.price ??
        0) > (previousPrice ?? 0);
    return isHigher
      ? $activeSiteConfig.alternateColourMode
        ? 'bg-yellow-100'
        : 'bg-green-100'
      : 'bg-red-100';
  }

  let flucsByDay: Map<string, Fluc[]> = new Map();
  $: {
    // group the flucs by the day of their lastUpdated
    // group them in reverse order, so that the most recent flucs are at the top
    if (marketData?.data?.bookmakerMarket?.[marketType].flucs) {
      flucsByDay = new Map();
      marketData.data?.bookmakerMarket[marketType].flucs
        ?.slice()
        .reverse()
        .forEach((fluc) => {
          const day = fluc.lastUpdated.toDateString();
          if (!day) return;
          if (!fluc.price || fluc.price < 1.02) return;
          if (flucsByDay.has(day)) {
            flucsByDay.get(day)?.push(fluc);
          } else {
            flucsByDay.set(day, [fluc]);
          }
        });
    }
  }

  export async function fetchMarketData() {
    if (!$market || !session || !$isSubscribed) return false;
    marketData = await fetchBookmakerMarket(
      fetch,
      { accessToken: session.access_token },
      $market.id
    );
    return true;
  }

  async function handlePlaceBet() {
    const prefillConfig = user?.data?.user.siteConfig.prefill;

    // ignore if disabled or not subscribed
    if (!isSubscribed || !prefillConfig?.quickFill) return;

    if ($market?.bookmaker && !prefillBookmakers.includes($market.bookmaker))
      return;

    // craft a link to the bookmaker's website
    // find the bookmaker navlink
    const navLink = raceLinks?.find((link) => {
      return link.bookmaker === $market?.bookmaker;
    });
    if (!navLink?.navLink || !$market) return;
    const url = new URL(navLink.navLink);
    // add query params (bwSelection)
    url.searchParams.append('bwSelection', runner.number.toString());
    let raceTypeConfig = prefillConfig?.defaults?.thoroughbred;
    if ($race?.meeting.type === 'Harness') {
      raceTypeConfig = prefillConfig?.defaults?.harness;
    } else if ($race?.meeting.type === 'Greyhound') {
      raceTypeConfig = prefillConfig?.defaults?.greyhound;
    }
    let stake = raceTypeConfig?.value ?? 0;
    if (raceTypeConfig?.type === 'TO_WIN') {
      const toWin = raceTypeConfig?.value ?? 0;
      stake = toWin / ($market[marketType].price - 1);
    }

    // round down to nearest whole number
    stake = Math.floor(stake);
    if (stake === 0) {
      stake = 1;
    }
    url.searchParams.append('bwStake', stake.toString());

    // open the link in a new tab
    window.open(url.toString(), '_blank');
  }

  function handleCreatePriceAlert(event: Event) {
    // event.stopPropagation();
    if (!$market || !$race) return;

    if (!$isSubscribed) {
      toast('You must be subscribed to create alerts!', {
        icon: '🔒',
        duration: 2000,
        position: 'bottom-center'
      });
      return;
    }

    clearPriceAlert();
    populatePriceAlert({
      runner: runner,
      bookmakers: [$market.bookmaker],
      alertType: 'GREATER_THAN_OR_LESS_THAN',
      eventId: $race.id,
      value: $market[marketType].price ?? 0,
      runnerId: runner.id,
      priceType: marketType === 'fixedWin' ? 'FIXED_WIN' : 'FIXED_PLACE',
      priceIds: [$market.id]
    });
  }
</script>

{#if showDialog}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    on:click|stopPropagation
    use:floatingContent
    class="absolute z-40 flex max-h-72 flex-col overflow-y-auto rounded-md border border-gray-200 bg-white px-4 py-2 font-normal shadow-sm shadow-gray-300"
  >
    <!-- {#if prefillEnabled && $hasCompanion && $race && !isRaceClosed($race) && $market?.bookmaker && prefillBookmakers.includes($market.bookmaker)}
      <Button
        size="small"
        class="mb-2"
        variant="solid"
        on:click={handlePlaceBet}
      >
        Fill Bet
      </Button>
    {/if} -->
    {#if $race && !isRaceClosed($race)}
      <Button
        size="small"
        class="mb-2"
        variant="outline"
        on:click={handleCreatePriceAlert}
      >
        Create Price Alert
      </Button>
    {/if}
    <div class="flex flex-row place-items-center self-center pb-1">
      <TablerChartLine />
      <span class="pl-1 font-bold">{$market?.bookmaker} Flucs</span>
    </div>
    <span class="self-center pb-1 italic">
      {runner.number}. {runner?.name}</span
    >

    {#if loading}
      <BarLoader color="purple" duration="2s" />
    {:else if !$isSubscribed}
      <span class="text-gray-500">Subscribe to view price flucs</span>
    {:else if marketData}
      <div class="">
        <div class="relative flex justify-center">
          <FlucChart
            placement="bottom"
            data={flucs}
            width={100}
            {strokeColour}
            hoverColour={strokeColour}
            strokeWidth={2}
          />

          <span
            class="absolute left-0 {pos()}-0 pointer-events-none rounded-md bg-white/80 px-0.5 text-[10px]"
            >{change > 0 ? '↑' : change < 0 ? '↓' : '~'}{change}%</span
          >
        </div>
        {#each [...flucsByDay] as [date, fbd], i (date)}
          <span class="font-semibold text-gray-700">{date}</span>
          {#each fbd as fluc (fluc)}
            <div class="flex flex-row {flucRowClass(fluc)} pl-1 pr-1">
              <span class="flex-1 text-left italic"
                >{formatTime(fluc.lastUpdated, true)}</span
              ><span class="font-semibold">{fluc?.price}</span>
            </div>
          {/each}
        {/each}
        <!-- Print opening price on first iteration -->
        {#if ($market?.[marketType]?.openingPrice ?? 0) > 1.01}
          <div class="flex flex-row bg-yellow-100 pl-1 pr-1">
            <span class="flex-1 text-left italic">Open</span><span
              class="font-semibold">{$market?.[marketType].openingPrice}</span
            >
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}
